import React, { useContext, useEffect, useState } from 'react';
import { withFirebase, FirebaseContext } from '../../firebase';
import { AuthUserContext } from '../../session';
import { CChartLine } from '@coreui/react-chartjs';
import { registerables, Chart } from 'chart.js';
import axios from 'axios';

Chart.register(...registerables);

const GoogleFitStepsChart = () => {
  const firebase = useContext(FirebaseContext);
  const authUser = useContext(AuthUserContext);
  const [stepsData, setStepsData] = useState([]);
  const [labels, setLabels] = useState([]);
  const [fitData, setFitData] = useState(null);

  const [error, setError] = useState(null);
  const fhirEndpoint = 'https://fhir.mieros.cloud/fhir/Observation';
  
  useEffect(() => {
    // Fetch the Google OAuth access token from local storage
    const userSession = JSON.parse(localStorage.getItem('userSession'));

    
    if (userSession && userSession.mieGoogleToken) {
      fetchGoogleFitData(userSession.mieGoogleToken);
    } else {
      setError('Google OAuth token not found. Please sign in again. User is not authenticated or Firebase is not initialized.');
    }
  }, [authUser, firebase]);

  const fetchGoogleFitData = async (accessToken0) => {
    try {
      const accessToken = localStorage.getItem('googleAccessToken');
      //console.log("Logging token fit: " + accessToken);

      if (!accessToken) {
        setError('Google OAuth token not found. Please sign in again.');
        return;
      }
      const headers = {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      };

      const requestBody = {
        aggregateBy: [{
          dataTypeName: "com.google.step_count.delta",
          dataSourceId: "derived:com.google.step_count.delta:com.google.android.gms:estimated_steps"
        }],
        bucketByTime: { durationMillis: 86400000 },
        startTimeMillis: Date.now() - (30 * 24 * 60 * 60 * 1000), // Last 30 days
        endTimeMillis: Date.now(),
      };

      const response = await fetch('https://www.googleapis.com/fitness/v1/users/me/dataset:aggregate', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error response: ", errorData);
        throw new Error(errorData.error.message);
      }

      const data = await response.json();
      
      // Process the data and update the state
      const formattedLabels = data.bucket.map(item => formatDate(item.startTimeMillis));
      const formattedSteps = data.bucket.map(item => item.dataset[0].point[0]?.value[0].intVal || 0);
      //console.log("Fetched Google Fit formattedLabels: ", formattedLabels);
      setLabels(formattedLabels);
      setStepsData(formattedSteps);
      setFitData(data);

      // Send step data as FHIR Observation
      //await sendStepsToFHIR(data);

    } catch (err) {
      setError('Error fetching Google Fit data');
      console.error(err);
    }
  };


  const sendStepsToFHIR = async () => {
    if (!fitData) {
      setError("No data available to export.");
      return;
    }
  
    console.log('fitData:', fitData);
  
    try {
      for (const bucket of fitData.bucket) {
        const dateMillis = bucket.startTimeMillis;
        const stepCount = bucket.dataset[0].point.reduce((total, point) => {
          return total + point.value[0].intVal;
        }, 0);
  
        const observationDate = new Date(parseInt(dateMillis)).toISOString();
        
        // Check if the observation already exists in FHIR
        const existingObservation = await checkExistingObservation(observationDate);
  
        if (!existingObservation) {
          // If no observation exists, create a new one
          const observation = {
            resourceType: 'Observation',
            status: 'final',
            identifier: [{
              system: "https://fhir.mieros.cloud/fhir/ids",
              value: authUser.authUser.uid
            }],
            category: [
              {
                coding: [
                  {
                    system: 'http://terminology.hl7.org/CodeSystem/observation-category',
                    code: 'activity',
                    display: 'Activity',
                  },
                ],
              },
            ],
            code: {
              coding: [
                {
                  system: 'http://loinc.org',
                  code: '55423-8',
                  display: 'Number of steps in 24 hour Measured',
                },
              ],
            },
            effectiveDateTime: observationDate,
            valueQuantity: {
              value: stepCount,
              unit: 'steps',
              system: 'http://unitsofmeasure.org',
              code: 'steps',
            },
          };
  
          console.log("Creating observation: " + JSON.stringify(observation));
          const response = await axios.post(`${fhirEndpoint}`, observation, {
            headers: {
              'Content-Type': 'application/fhir+json',
            },
          });
  
          console.log('FHIR Observation created:', response);
        } else {
          console.log(`Skipping duplicate observation for ${observationDate}`);
        }
      }
    } catch (err) {
      console.error('Error sending step data to FHIR:', err);
    }
  };
  
  // Function to check if an observation for the same date already exists
  const checkExistingObservation = async (observationDate) => {
    try {
      const token = localStorage.getItem('mieFToken');
      const response = await axios.get(`${fhirEndpoint}?date=${observationDate}&identifier=https://fhir.mieros.cloud/fhir/ids|${authUser.authUser.uid}`, {
        headers: {
          'Authorization': "Bearer " + encodeURIComponent(token),
          'Content-Type': 'application/fhir+json',
        },
      });
      console.log(' observation response:', response);
      if (response.data.entry && response.data.entry.length > 0) {
        console.log('Existing observation found:', response.data);
        return true; // Observation exists
      } else {
        console.log('No Existing observation found:', response.data);
        return false; // No existing observation
      }
    } catch (error) {
      console.error('Error checking for existing observation:', error);
      return false;
    }
  };
  


  // Process the fetched Google Fit data into labels and steps
  const processGoogleFitData = (fitData) => {
    const labels = [];
    const steps = [];
  
    if (fitData.bucket) {
      fitData.bucket.forEach(bucket => {
        const dateMillis = bucket.startTimeMillis;
        const date = new Date(parseInt(dateMillis));
  
        // Use bucket data for steps count
        const stepCount = bucket.dataset[0].point.reduce((total, point) => {
          return total + point.value[0].intVal;
        }, 0);
  
        labels.push(formatDate(dateMillis)); // Pass dateMillis directly
        steps.push(stepCount);
      });
  
      setLabels(labels);
      setStepsData(steps);
    } else {
      setError('No step data available.');
    }
  };
  

  const formatDate = (dateMillis) => {
    const date = new Date(parseInt(dateMillis)); // Ensure it's converted to a number
    if (isNaN(date.getTime())) {
      return 'Invalid Date';  // Handle any invalid dates
    }
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  };

  const chartData = {
    labels: labels.length > 0 ? labels : ["No Data"], // Ensure no empty labels
    datasets: [
      {
        label: 'Steps',
        data: stepsData.length > 0 ? stepsData : [0], // Ensure no empty data,
        fill: false,
        backgroundColor: 'rgba(75,192,192,0.4)',
        borderColor: 'rgba(75,192,192,1)',
      },
    ],
  };

  const options = {
    scales: {
      x: {
        type: 'category', // Change to 'category' since we're using string labels
      },
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <div>
      <h2>Steps Last Month</h2>
      {error && <p>Error: {error}</p>}
      <CChartLine data={chartData} options={options} />
    </div>
  );
};

export default withFirebase(GoogleFitStepsChart);
